import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/api";

export default function Portal() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [tab, setTab] = useState("packages");
    const [paying, setPaying] = useState(null);

    const load = () => api.get(`/portal/${token}`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Portal unavailable"));

    useEffect(() => { load(); }, [token]);

    const payInvoice = async (iid) => {
        setPaying(iid);
        try {
            const { data: d } = await api.post(`/portal/${token}/invoices/${iid}/checkout`);
            window.location.href = d.checkout_url;
        } catch { toast.error("Could not open checkout"); setPaying(null); }
    };

    if (err) return <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--gt-bg)" }}>
        <div className="gt-card p-8 text-center max-w-md"><div style={{ fontSize: 36, color: "var(--gt-red)" }}><i className="fa-solid fa-triangle-exclamation"></i></div>
            <h1 className="font-display text-2xl mt-3 mb-2">Portal unavailable</h1>
            <p className="text-sm" style={{ color: "var(--gt-text-2)" }}>{err}</p>
        </div></div>;

    if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--gt-bg)", color: "var(--gt-text-2)" }}>Loading portal…</div>;

    const unpaid = (data.invoices || []).filter((i) => i.status !== "paid");
    const paid = (data.invoices || []).filter((i) => i.status === "paid");

    return (
        <div className="min-h-screen" style={{ background: "var(--gt-bg)" }}>
            <section className="pt-16 pb-12" style={{ borderBottom: "1px solid var(--gt-border)" }}>
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-start justify-between flex-wrap gap-4">
                        <div>
                            <div className="overline mb-3">Buyer Portal · {data.client.region || "—"}</div>
                            <h1 className="font-display text-4xl lg:text-5xl mb-2" data-testid="portal-title">{data.client.organization}</h1>
                            <div className="text-sm" style={{ color: "var(--gt-text-2)" }}>{data.client.name} · <a href={`mailto:${data.client.email}`} className="gt-link">{data.client.email}</a></div>
                        </div>
                        <a href="/" className="gt-btn-ghost px-4 py-2 text-sm">First Landing Studio home</a>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
                        {[
                            { k: data.packages.length, v: "Packages delivered" },
                            { k: unpaid.length, v: "Open invoices" },
                            { k: paid.length, v: "Paid invoices" },
                            { k: data.inquiries.length, v: "License requests" },
                        ].map((s, i) => (
                            <div key={i} className="gt-card p-4" data-testid={`portal-stat-${i}`}>
                                <div className="font-display text-3xl" style={{ color: "var(--gt-accent)" }}>{s.k}</div>
                                <div className="overline mt-1" style={{ fontSize: 10 }}>{s.v}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex gap-2 mb-8 overflow-x-auto">
                        {[
                            { id: "packages", l: `Packages (${data.packages.length})` },
                            { id: "invoices", l: `Invoices (${data.invoices.length})` },
                            { id: "requests", l: `Requests (${data.inquiries.length})` },
                        ].map((t) => (
                            <button key={t.id} onClick={() => setTab(t.id)} className="px-4 py-2 text-sm whitespace-nowrap"
                                style={{ background: tab === t.id ? "var(--gt-overlay)" : "transparent", border: "1px solid var(--gt-border)", color: tab === t.id ? "var(--gt-text)" : "var(--gt-text-2)" }}
                                data-testid={`portal-tab-${t.id}`}>
                                {t.l}
                            </button>
                        ))}
                    </div>

                    {tab === "packages" && (
                        <div className="space-y-4">
                            {data.packages.map((p) => (
                                <div key={p.id} className="gt-card p-5 grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid={`portal-pkg-${p.id}`}>
                                    <div className="lg:col-span-7">
                                        <div className="font-display text-xl">{p.name}</div>
                                        <div className="overline mt-1" style={{ fontSize: 10 }}>{p.image_ids?.length || 0} images · {new Date(p.created_at).toLocaleDateString()}</div>
                                        <div className="flex gap-2 mt-3 flex-wrap">
                                            {(p.images || []).slice(0, 4).map((im) => (
                                                <img key={im.id} src={im.image_url || `${BACKEND_URL}/api/archive/image/${im.id}`} alt="" className="w-16 h-16 object-cover" />
                                            ))}
                                        </div>
                                    </div>
                                    <div className="lg:col-span-5 flex items-end justify-end gap-2 flex-wrap">
                                        <a href={`/pkg/${p.download_token}`} target="_blank" rel="noreferrer" className="gt-btn-ghost px-4 py-2 text-xs">
                                            <i className="fa-solid fa-box-open mr-1"></i> Open package
                                        </a>
                                        <a href={`${BACKEND_URL}/api/packages/download/${p.download_token}/dossier.pdf`} target="_blank" rel="noreferrer" className="gt-btn-primary px-4 py-2 text-xs" data-testid={`portal-pkg-pdf-${p.id}`}>
                                            <i className="fa-solid fa-file-pdf mr-1"></i> Dossier PDF
                                        </a>
                                    </div>
                                </div>
                            ))}
                            {data.packages.length === 0 && <div className="gt-card p-8 text-center" style={{ color: "var(--gt-text-2)" }}>No packages delivered yet.</div>}
                        </div>
                    )}

                    {tab === "invoices" && (
                        <div className="space-y-3">
                            {data.invoices.map((inv) => (
                                <div key={inv.id} className="gt-card p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center" data-testid={`portal-inv-${inv.id}`}>
                                    <div className="lg:col-span-6">
                                        <div className="overline" style={{ fontSize: 10 }}>{new Date(inv.created_at).toLocaleDateString()}</div>
                                        <div className="font-display text-lg mt-1">{inv.description}</div>
                                    </div>
                                    <div className="lg:col-span-2 font-mono text-2xl" style={{ color: "var(--gt-accent)" }}>${inv.amount_usd}</div>
                                    <div className="lg:col-span-2 overline" style={{ fontSize: 10, color: inv.status === "paid" ? "var(--gt-ok)" : "var(--gt-warn)" }}>
                                        {inv.status === "paid" ? "Paid" : "Awaiting payment"}
                                    </div>
                                    <div className="lg:col-span-2 flex justify-end">
                                        {inv.status !== "paid" && (
                                            <button onClick={() => payInvoice(inv.id)} disabled={paying === inv.id} className="gt-btn-primary px-4 py-2 text-xs" data-testid={`portal-pay-${inv.id}`}>
                                                {paying === inv.id ? "Opening…" : <><i className="fa-solid fa-credit-card mr-1"></i> Pay now</>}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {data.invoices.length === 0 && <div className="gt-card p-8 text-center" style={{ color: "var(--gt-text-2)" }}>No invoices on record.</div>}
                        </div>
                    )}

                    {tab === "requests" && (
                        <div className="space-y-3">
                            {data.inquiries.map((r) => (
                                <div key={r.id} className="gt-card p-5">
                                    <div className="overline" style={{ fontSize: 10 }}>{r.kind} · {new Date(r.created_at).toLocaleDateString()}</div>
                                    <p className="text-sm mt-2">{r.message}</p>
                                </div>
                            ))}
                            {data.inquiries.length === 0 && <div className="gt-card p-8 text-center" style={{ color: "var(--gt-text-2)" }}>No open requests.</div>}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
